// src/pages/RecipientPage.js - Updated with date and time display
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Typography, Button, Stack, Paper, Chip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RecipientTable from '../components/RecipientTable';
import { fetchRecipients, fetchRecipientsSummary, fetchRecipientsForExport } from '../slices/recipientSlice';
import { motion } from 'framer-motion';
import { exportToExcel, exportToPrintWindow, exportVoucherToPrint } from '../utils/exportUtils';

const MotionPaper = motion(Paper);

const today = new Date().toISOString().slice(0, 10);
const DEFAULT_FILTERS = {
  student_name: '', parent_name: '', parent_phone: '', class_name: '',
  method: '', number: '', dateFrom: today, dateTo: today,
};

// Maps the table's filter field names to the recipients API's query params.
const toApiFilters = (filterValues) => ({
  number: filterValues.number,
  student_name: filterValues.student_name,
  parent_name: filterValues.parent_name,
  parent_phone: filterValues.parent_phone,
  class_name: filterValues.class_name,
  payment_type: filterValues.method,
  date_from: filterValues.dateFrom,
  date_to: filterValues.dateTo,
});

const RecipientPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const recipients = useSelector((state) => state.recipients.list);
  const rowCount = useSelector((state) => state.recipients.count);
  const status = useSelector((state) => state.recipients.status);
  const summary = useSelector((state) => state.recipients.summary);

  const [filterValues, setFilterValues] = useState(() => {
    try {
      const saved = localStorage.getItem('recipient_table_filters');
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
    } catch {
      return DEFAULT_FILTERS;
    }
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filterValues);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Debounce free-text/date filters so we don't hit the API on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedFilters(filterValues), 400);
    return () => clearTimeout(handle);
  }, [filterValues]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const apiFilters = useMemo(() => toApiFilters(debouncedFilters), [debouncedFilters]);

  const refetch = useCallback(() => {
    dispatch(fetchRecipients({ ...apiFilters, page: paginationModel.page, pageSize: paginationModel.pageSize }));
  }, [dispatch, apiFilters, paginationModel]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    dispatch(fetchRecipientsSummary(apiFilters));
  }, [dispatch, apiFilters]);

  const handleFilterChange = (field, value) => {
    setFilterValues((prev) => {
      const next = { ...prev, [field]: value };
      localStorage.setItem('recipient_table_filters', JSON.stringify(next));
      return next;
    });
  };

  const handleDeleted = () => {
    refetch();
    dispatch(fetchRecipientsSummary(apiFilters));
  };

  const getExportRows = async () => {
    return dispatch(fetchRecipientsForExport(apiFilters)).unwrap();
  };

  const handleExportExcel = async () => {
    const columns = [
      { header: 'رقم السند', key: 'number' },
      { header: 'اسم الطالب', key: 'student_name' },
      { header: 'اسم ولي الأمر', key: 'parent_name' },
      { header: 'الهاتف', key: 'parent_phone' },
      { header: 'الصف', key: 'class_name' },
      { header: 'المبلغ', key: 'amount' },
      { header: 'التاريخ والوقت', key: 'datetime_display' },
      { header: 'التاريخ', key: 'date' },
      { header: 'الوقت', key: 'time_display' },
      { header: 'طريقة الدفع', key: 'payment_type' },
      { header: 'حالة الاستلام', key: 'received_status' },
    ];
    const rows = await getExportRows();
    const data = rows.map(r => ({
      number: r.number,
      student_name: r.student_name,
      parent_name: r.parent_name,
      parent_phone: r.parent_phone,
      class_name: r.class_name,
      amount: r.amount,
      datetime_display: r.datetime_display || `${r.date || ''} ${r.time_display || ''}`.trim(),
      date: r.date,
      time_display: r.time_display,
      payment_type: r.payment_type,
      received_status: r.received ? 'تم الاستلام' : 'لم يتم الاستلام',
    }));
    exportToExcel(data, columns, 'recipients');
  };

  const handlePrint = async () => {
    const filtered = await getExportRows();
    if (filtered.length === 1) {
      exportVoucherToPrint(filtered[0], { type: 'سند قبض' });
    } else {
      const columns = [
        { header: 'رقم السند', key: 'number' },
        { header: 'المبلغ', key: 'amount' },
        { header: 'طريقة الدفع', key: 'payment_type' },
        { header: 'اسم الطالب', key: 'student_name' },
        { header: 'اسم ولي الأمر', key: 'parent_name' },
        { header: 'الهاتف', key: 'parent_phone' },
        { header: 'التاريخ والوقت', key: 'datetime_display' },
      ];
      const data = filtered.map(r => ({
        number: r.number,
        amount: r.amount,
        payment_type: r.payment_type,
        student_name: r.student_name,
        parent_name: r.parent_name,
        parent_phone: r.parent_phone,
        datetime_display: r.datetime_display || `${r.date || ''} ${r.time_display || ''}`.trim(),
      }));
      await exportToPrintWindow(data, columns, 'قائمة سندات القبض', { summary: { amountKey: 'amount' } });
    }
  };

  // Latest receipt among the currently loaded page (rows are ordered by
  // number desc server-side, so this matches the true latest in the common
  // case of viewing the first page with no filters narrowing it out).
  const getLatestRecipientDate = () => {
    if (!recipients || recipients.length === 0) return '-';
    const sorted = [...recipients].sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.time || '00:00:00'}`);
      const dateB = new Date(`${b.date} ${b.time || '00:00:00'}`);
      return dateB - dateA;
    });
    return sorted[0]?.datetime_display || sorted[0]?.date || '-';
  };

  const summaryCount = summary.count || 0;
  const summaryTotal = summary.total_amount || 0;

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إدارة سندات القبض
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إنشاء وإدارة وتصدير سندات القبض المالية
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/students/payment')}
            data-guide-id="guide-add-recipient"
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة سند قبض
          </Button>
        </MotionPaper>

        {/* Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        >
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                flex: 1,
                minWidth: 180,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ bgcolor: '#EEF2FF', color: '#4338CA', borderRadius: 2.5, width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>عدد السندات</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>{summaryCount}</Typography>
              </Box>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                flex: 1,
                minWidth: 180,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ bgcolor: '#DCFCE7', color: '#16A34A', borderRadius: 2.5, width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PaymentsIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>مجموع المبالغ</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#16A34A', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  {summaryTotal.toLocaleString('en-US')}
                </Typography>
              </Box>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 2.5 },
                flex: 1,
                minWidth: 180,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ bgcolor: '#FEF3C7', color: '#D97706', borderRadius: 2.5, width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccessTimeIcon />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>آخر سند</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#1E1B4B', fontSize: '0.9rem', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                  {getLatestRecipientDate()}
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </motion.div>

        {/* Export Actions */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: '#1E1B4B',
              fontFamily: 'Cairo, Tajawal, sans-serif',
            }}
          >
            تصدير القائمة
          </Typography>

          <Stack useFlexGap
            direction="row"
            spacing={1.5}
            sx={{ flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, alignItems: 'center', gap: 1.5 }}
          >
            <Button
              variant="outlined"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                gap: 1,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#16A34A',
                color: '#16A34A',
                '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
              }}
            >
              تصدير Excel
              <Chip
                label={`${summaryCount} سند`}
                size="small"
                sx={{
                  bgcolor: '#DCFCE7',
                  color: '#16A34A',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 20,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePrint}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                gap: 1,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                borderColor: '#4338CA',
                color: '#4338CA',
                '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
              }}
            >
              طباعة
              <Chip
                label={`${summaryCount} سند`}
                size="small"
                sx={{
                  bgcolor: '#EEF2FF',
                  color: '#4338CA',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 20,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              />
            </Button>
          </Stack>
        </MotionPaper>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
        >
          <RecipientTable
            data={recipients}
            rowCount={rowCount}
            loading={status === 'loading'}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            onDeleted={handleDeleted}
          />
        </motion.div>
      </Box>
    </Box>
  );
};

export default RecipientPage;
